import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Home, FileText, CalendarDays, LifeBuoy, LogOut, LayoutGrid } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { toast } from 'sonner';
import { ClientAuth } from '../components/client/ClientAuth';
import { ClientHome } from '../components/client/ClientHome';
import { ClientInvoices } from '../components/client/ClientInvoices';
import { ClientInterventions } from '../components/client/ClientInterventions';
import { ClientTickets } from '../components/client/ClientTickets';
import { InstallAppCard } from '../components/InstallAppCard';
import { getClientToken, getStoredClient, clearClientSession, clientGet, storeClientSession } from '../lib/clientApi';

const LOGO = 'https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png';

const TABS = [
  { id: 'accueil', label: 'Accueil', icon: LayoutGrid },
  { id: 'factures', label: 'Factures', icon: FileText },
  { id: 'interventions', label: 'Interventions', icon: CalendarDays },
  { id: 'tickets', label: 'Tickets', icon: LifeBuoy },
];

export default function ClientPage() {
  const [client, setClient] = useState(() => (getClientToken() ? getStoredClient() : null));
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'accueil';
  const go = useCallback((id) => setParams({ tab: id }), [setParams]);

  const logout = useCallback(() => {
    clearClientSession();
    setClient(null);
    toast.info('Vous êtes déconnecté');
  }, []);

  const onUnauthorized = useCallback(() => {
    clearClientSession();
    setClient(null);
    toast.error('Session expirée, reconnectez-vous');
  }, []);

  useEffect(() => {
    if (!getClientToken()) return;
    clientGet('/api/client/me')
      .then((r) => { setClient(r.data); storeClientSession(getClientToken(), r.data); })
      .catch((err) => { if ([401, 403].includes(err?.response?.status)) onUnauthorized(); });
  }, [onUnauthorized]);

  if (!client) {
    return (
      <>
        <SEOHead title="Espace client — Connexion" description="Accès réservé aux clients Ptitservices." path="/client" noindex />
        <InstallAppCard portal="client" headOnly />
        <ClientAuth onLogin={(c) => { setClient(c); navigate('/client?tab=accueil', { replace: true }); }} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))]/40 flex flex-col" data-testid="client-dashboard">
      <SEOHead title="Espace client" description="Espace client Ptitservices." path="/client" noindex />
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur border-b border-border">
        <div className="h-1 w-full bg-[hsl(var(--brand-apricot))]" aria-hidden="true" />
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <img src={LOGO} alt="Ptitservices.com" className="h-8 w-auto rounded-md" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--brand-apricot))] font-semibold leading-none">Espace client</p>
            <p className="font-semibold truncate leading-tight text-base" data-testid="client-user-name">{client.name}</p>
          </div>
          <Link to="/" aria-label="Retour au site" className="p-2 rounded-md hover:bg-secondary min-h-[44px] min-w-[44px] flex items-center justify-center" data-testid="client-home-link"><Home className="h-5 w-5" /></Link>
          <Button variant="ghost" size="icon" className="h-11 w-11" onClick={logout} aria-label="Se déconnecter" data-testid="client-logout"><LogOut className="h-5 w-5" /></Button>
        </div>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-5 pb-28">
        {tab === 'accueil' && <div className="mb-4"><InstallAppCard portal="client" /></div>}
        {tab === 'accueil' && <ClientHome client={client} go={go} onUnauthorized={onUnauthorized} />}
        {tab === 'factures' && <ClientInvoices onUnauthorized={onUnauthorized} />}
        {tab === 'interventions' && <ClientInterventions onUnauthorized={onUnauthorized} />}
        {tab === 'tickets' && <ClientTickets onUnauthorized={onUnauthorized} />}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} aria-label="Navigation espace client">
        <div className="max-w-2xl mx-auto grid grid-cols-4 px-2">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button key={t.id} type="button" onClick={() => go(t.id)} aria-current={active ? 'page' : undefined} data-testid={`client-tab-${t.id}`}
                className={`relative flex flex-col items-center justify-center gap-1 min-h-[60px] text-xs font-semibold transition-colors ${active ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground hover:text-foreground'}`}>
                <t.icon className={`h-5 w-5 ${active ? 'scale-110' : ''} transition-transform`} />
                {t.label}
                {active && <span className="absolute bottom-0 h-0.5 w-10 rounded-full bg-[hsl(var(--primary))]" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
